
import { useEffect, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { motion, AnimatePresence } from "framer-motion";
import { createTaskApi, getEmployeesApi, getMyReportingApi } from "../../app/api";
import { createPortal } from "react-dom";
import {
  AlignLeft,
  CalendarDays,
  Check,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Flag,
  Search,
  Sparkles,
  UserPlus,
  Users,
} from "lucide-react";

const IMAGE_BASE_URL = "https://uptask.visiontrek.io";

const PRIORITY_COLORS = {
  LOW: "bg-emerald-50 text-emerald-700 border-emerald-300 shadow-[0_12px_28px_rgba(16,185,129,0.16)] ring-2 ring-emerald-100",
  MEDIUM: "bg-amber-50 text-amber-700 border-amber-300 shadow-[0_12px_28px_rgba(245,158,11,0.18)] ring-2 ring-amber-100",
  HIGH: "bg-rose-50 text-rose-700 border-rose-300 shadow-[0_12px_28px_rgba(244,63,94,0.16)] ring-2 ring-rose-100",
};

const toLocalDateKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatDisplayDate = (dateKey) => {
  if (!dateKey) return "Select deadline";
  const date = new Date(`${dateKey}T00:00:00`);
  return date.toLocaleDateString("en-IN", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const buildCalendarDays = (monthDate) => {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const firstDay = new Date(year, month, 1);
  const startOffset = firstDay.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const previousMonthDays = new Date(year, month, 0).getDate();
  const cells = [];

  for (let i = startOffset - 1; i >= 0; i -= 1) {
    cells.push({
      date: new Date(year, month - 1, previousMonthDays - i),
      muted: true,
    });
  }

  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push({ date: new Date(year, month, day), muted: false });
  }

  while (cells.length % 7 !== 0 || cells.length < 42) {
    const nextDay = cells.length - startOffset - daysInMonth + 1;
    cells.push({
      date: new Date(year, month + 1, nextDay),
      muted: true,
    });
  }

  return cells;
};

const getUserImageUrl = (user = {}) => {
  const path = user.profile_pic || user.image || user.avatar || user.photo || "";
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  return `${IMAGE_BASE_URL}${path.startsWith("/") ? path : `/${path}`}`;
};

export default function CreateTaskModal({ open, onClose, onCreated }) {

  const [search, setSearch] = useState("");
  const user = JSON.parse(localStorage.getItem("user"));
  const currentUserId = Number(user?.id || user?.user_id);
  const isAdmin = [1, 2, 3].includes(Number(user?.role_id));

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("MEDIUM");
  const [assignedTo, setAssignedTo] = useState([]);
  const [users, setUsers] = useState([]);
  const [assignScopeReady, setAssignScopeReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [dueDate, setDueDate] = useState("");
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(() => new Date());

  // useEffect(() => {
  //   if (!open) return;
  //   getEmployeesApi()
  //     .then((res) => setUsers(res.data || []))
  //     .catch(() => setUsers([]));
  // }, [open]);


  useEffect(() => {
    if (!open) return;

    if (!isAdmin && currentUserId) {
      setAssignedTo((selected) => (selected.length ? selected : [currentUserId]));
    }

    const loadAssignableUsers = async () => {
      setAssignScopeReady(false);
      setUsers([]);

      try {
        if (isAdmin) {
          const res = await getEmployeesApi();
          const allUsers = res.data || [];
          setUsers(allUsers.filter((u) => Number(u.role_id) === 6));
          return;
        }

        const res = await getMyReportingApi();
        const teamMembers = res?.success ? (res.data?.teamMembers || []) : [];
        setUsers(teamMembers.filter((member) => String(member.id) !== String(user?.id)));
      } catch {
        setUsers([]);
      } finally {
        setAssignScopeReady(true);
      }
    };

    loadAssignableUsers();
  }, [open, isAdmin, currentUserId, user?.id]);

  const handleSubmit = async () => {
    if (!title.trim()) {
      setError("Task title is required");
      return;
    }

    if (!dueDate) {
      setError("Due date is required");
      return;
    }

    if (assignedTo.length === 0) {
      setError("Select at least one team member");
      return;
    }

    const payload = {
      title,
      description,
      priority,
      due_date: dueDate,   // ✅ FIXED
      assigned_to: assignedTo,
    };

    setLoading(true);
    setError(null);

    try {
      const res = await createTaskApi(payload);
      if (!res.success) throw new Error();

      onCreated();
      onClose();

      setTitle("");
      setDescription("");
      setPriority("MEDIUM");
      setAssignedTo([]);
      setDueDate("");   // 👈 ADD HERE
      setCalendarOpen(false);
    } catch {
      setError("Failed to create task");
    } finally {
      setLoading(false);
    }
  };

  const todayKey = toLocalDateKey(new Date());
  const calendarDays = buildCalendarDays(calendarMonth);
  const selectedDate = dueDate ? new Date(`${dueDate}T00:00:00`) : null;
  const calendarTitle = calendarMonth.toLocaleDateString("en-IN", {
    month: "long",
    year: "numeric",
  });
  const currentUserName = [user?.first_name, user?.last_name].filter(Boolean).join(" ") || user?.name || "Myself";
  const assignableUsers = [
    {
      ...user,
      id: currentUserId,
      first_name: currentUserName,
      last_name: "",
      job_title: "Assign yourself",
      isSelfOption: true,
    },
    ...users.filter((u) => Number(u.id) !== currentUserId),
  ].filter((u) => Number(u.id));

  const selectDueDate = (date) => {
    const dateKey = toLocalDateKey(date);
    if (dateKey < todayKey) return;
    setDueDate(dateKey);
    setCalendarMonth(new Date(date.getFullYear(), date.getMonth(), 1));
    setCalendarOpen(false);
  };

  // return (
  return createPortal(
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-50 flex">

          {/* Overlay */}
          <motion.div
            className="flex-1 bg-[#0c0620]/65 backdrop-blur-md"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          {/* Right Sheet */}
          <motion.div
            className="w-full max-w-[640px] overflow-hidden rounded-l-[30px] border-l border-white/50 bg-[#f4f7fb] shadow-[0_30px_100px_rgba(7,10,30,0.46)] flex flex-col"
            initial={{ x: 600, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 600, opacity: 0 }}
            transition={{ type: "spring", stiffness: 90, damping: 20 }}
          >
            {/* Header */}
            <div className="relative overflow-hidden bg-[#120d28] text-white px-7 py-8">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_12%_12%,rgba(34,211,238,0.32),transparent_30%),radial-gradient(circle_at_86%_10%,rgba(139,92,246,0.44),transparent_35%),radial-gradient(circle_at_70%_90%,rgba(245,158,11,0.14),transparent_28%),linear-gradient(135deg,rgba(255,255,255,0.1),transparent_45%)]"></div>
              <div className="absolute -left-24 top-0 h-40 w-40 rounded-full bg-cyan-300/20 blur-3xl" />
              <div className="absolute -right-20 -top-16 h-44 w-44 rounded-full bg-violet-400/25 blur-3xl" />
              <div className="absolute inset-y-0 -left-40 w-40 rotate-12 bg-white/10 blur-xl animate-pulse" />
              <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-cyan-200/70 to-transparent"></div>
              <div className="relative flex justify-between items-start gap-4">
                <div className="flex items-start gap-4">
                  <div className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl border border-cyan-200/35 bg-cyan-300/14 shadow-[0_18px_45px_rgba(34,211,238,0.18),inset_0_1px_0_rgba(255,255,255,0.16)]">
                    <ClipboardList size={23} className="text-cyan-100" />
                  </div>
                  <div>
                  <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-cyan-200/80">
                    Task Planner
                  </p>
                  <h2 className="mt-1 text-2xl font-bold tracking-tight">
                    Create new task
                  </h2>
                  <p className="text-sm text-white/70 mt-1">
                    Plan, assign and track work
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-white/12 bg-white/10 px-3 py-1 text-[11px] font-bold text-white/80">
                      <Sparkles size={13} className="text-cyan-200" />
                      Smart assignment
                    </span>
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-white/12 bg-white/10 px-3 py-1 text-[11px] font-bold text-white/80">
                      <Flag size={13} className="text-amber-200" />
                      Priority tracked
                    </span>
                  </div>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-full border border-white/20 bg-white/12 text-white shadow-lg transition hover:bg-white hover:text-[#120d28]"
                  aria-label="Close create task"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <motion.div
              className="flex-1 overflow-y-auto px-7 py-6 space-y-5 bg-[radial-gradient(circle_at_18%_0%,rgba(34,211,238,0.12),transparent_28%),linear-gradient(180deg,#f8fbff_0%,#eef3fb_100%)]"
              initial="hidden"
              animate="show"
              variants={{
                hidden: {},
                show: {
                  transition: {
                    staggerChildren: 0.08,
                  },
                },
              }}
            >
              {/* Title */}
              <motion.div
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  show: { opacity: 1, y: 0 },
                }}
                className="rounded-[24px] border border-white bg-white/88 p-4 shadow-[0_16px_42px_rgba(15,23,42,0.08)] ring-1 ring-slate-900/5 transition hover:-translate-y-0.5 hover:shadow-[0_22px_55px_rgba(15,23,42,0.1)]"
              >
                <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                  <ClipboardList size={16} className="text-cyan-600" />
                  Task title *
                </label>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="mt-3 w-full rounded-2xl bg-slate-50/90 border border-slate-200 px-4 py-3 text-slate-900 shadow-inner shadow-slate-200/40 placeholder:text-slate-400 transition focus:bg-white focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-cyan-300"
                  placeholder="e.g. Design onboarding flow"
                />
              </motion.div>

              {/* Description */}
              <motion.div
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  show: { opacity: 1, y: 0 },
                }}
                className="rounded-[24px] border border-white bg-white/88 p-4 shadow-[0_16px_42px_rgba(15,23,42,0.08)] ring-1 ring-slate-900/5 transition hover:-translate-y-0.5 hover:shadow-[0_22px_55px_rgba(15,23,42,0.1)]"
              >
                <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                  <AlignLeft size={16} className="text-cyan-600" />
                  Description
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={4}
                  className="mt-3 w-full resize-none rounded-2xl bg-slate-50/90 border border-slate-200 px-4 py-3 text-slate-900 shadow-inner shadow-slate-200/40 placeholder:text-slate-400 transition focus:bg-white focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-cyan-300"
                  placeholder="Optional details about the task"
                />
              </motion.div>












              {/* =========deadline ---------============== */}
              <motion.div
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  show: { opacity: 1, y: 0 },
                }}
                className="rounded-[24px] border border-white bg-white/88 p-4 shadow-[0_16px_42px_rgba(15,23,42,0.08)] ring-1 ring-slate-900/5 transition hover:-translate-y-0.5 hover:shadow-[0_22px_55px_rgba(15,23,42,0.1)]"
              >
                <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                  <CalendarDays size={16} className="text-cyan-600" />
                  Deadline *
                </label>

                <div className="relative mt-3">
                  <button
                    type="button"
                    onClick={() => setCalendarOpen((value) => !value)}
                    className={`flex w-full items-center justify-between rounded-2xl border px-4 py-3 text-left shadow-inner shadow-slate-200/40 transition ${
                      dueDate
                        ? "border-cyan-300 bg-white text-slate-900 ring-2 ring-cyan-100"
                        : "border-slate-200 bg-slate-50/90 text-slate-400 hover:bg-white hover:text-slate-700"
                    }`}
                  >
                    <span>
                      <span className="block text-sm font-black text-slate-800">
                        {formatDisplayDate(dueDate)}
                      </span>
                      <span className="mt-0.5 block text-xs font-semibold text-slate-400">
                        {dueDate ? "Deadline selected" : "Pick a date from the calendar"}
                      </span>
                    </span>
                    <span className="grid h-10 w-10 place-items-center rounded-2xl bg-cyan-50 text-cyan-600 shadow-sm">
                      <CalendarDays size={18} />
                    </span>
                  </button>

                  <AnimatePresence>
                    {calendarOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: 12, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 8, scale: 0.98 }}
                        transition={{ duration: 0.16 }}
                        className="absolute left-0 right-0 top-[calc(100%+10px)] z-30 overflow-hidden rounded-[26px] border border-white bg-white shadow-[0_24px_60px_rgba(15,23,42,0.2)] ring-1 ring-slate-900/8"
                      >
                        <div className="bg-gradient-to-br from-[#120d28] via-[#25134f] to-[#0e7490] px-4 py-4 text-white">
                          <div className="flex items-center justify-between">
                            <button
                              type="button"
                              onClick={() =>
                                setCalendarMonth(
                                  (current) =>
                                    new Date(current.getFullYear(), current.getMonth() - 1, 1)
                                )
                              }
                              className="grid h-9 w-9 place-items-center rounded-2xl border border-white/15 bg-white/10 text-white transition hover:bg-white hover:text-[#120d28]"
                              aria-label="Previous month"
                            >
                              <ChevronLeft size={18} />
                            </button>
                            <div className="text-center">
                              <p className="text-[10px] font-black uppercase tracking-[0.24em] text-cyan-100/80">
                                Deadline Calendar
                              </p>
                              <p className="mt-1 text-lg font-black">{calendarTitle}</p>
                            </div>
                            <button
                              type="button"
                              onClick={() =>
                                setCalendarMonth(
                                  (current) =>
                                    new Date(current.getFullYear(), current.getMonth() + 1, 1)
                                )
                              }
                              className="grid h-9 w-9 place-items-center rounded-2xl border border-white/15 bg-white/10 text-white transition hover:bg-white hover:text-[#120d28]"
                              aria-label="Next month"
                            >
                              <ChevronRight size={18} />
                            </button>
                          </div>
                        </div>

                        <div className="p-4">
                          <div className="grid grid-cols-7 gap-1.5 text-center text-[10px] font-black uppercase tracking-wider text-slate-400">
                            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                              <div key={day} className="py-1">
                                {day}
                              </div>
                            ))}
                          </div>
                          <div className="mt-2 grid grid-cols-7 gap-1.5">
                            {calendarDays.map(({ date, muted }) => {
                              const dateKey = toLocalDateKey(date);
                              const isPast = dateKey < todayKey;
                              const isToday = dateKey === todayKey;
                              const isSelected =
                                selectedDate && dateKey === toLocalDateKey(selectedDate);

                              return (
                                <button
                                  type="button"
                                  key={`${dateKey}-${muted ? "muted" : "active"}`}
                                  onClick={() => selectDueDate(date)}
                                  disabled={isPast}
                                  className={`relative grid aspect-square place-items-center rounded-2xl text-sm font-black transition ${
                                    isSelected
                                      ? "bg-gradient-to-br from-cyan-400 to-violet-600 text-white shadow-[0_12px_24px_rgba(79,70,229,0.3)]"
                                      : isPast
                                      ? "cursor-not-allowed bg-slate-50 text-slate-200"
                                      : muted
                                      ? "text-slate-300 hover:bg-slate-50"
                                      : "text-slate-700 hover:bg-cyan-50 hover:text-cyan-700"
                                  }`}
                                >
                                  {date.getDate()}
                                  {isToday && !isSelected && (
                                    <span className="absolute bottom-1.5 h-1 w-1 rounded-full bg-cyan-500" />
                                  )}
                                </button>
                              );
                            })}
                          </div>

                          <div className="mt-4 flex items-center justify-between gap-3">
                            <button
                              type="button"
                              onClick={() => {
                                const today = new Date();
                                selectDueDate(today);
                              }}
                              className="rounded-2xl bg-cyan-50 px-4 py-2 text-xs font-black text-cyan-700 transition hover:bg-cyan-100"
                            >
                              Today
                            </button>
                            <button
                              type="button"
                              onClick={() => setCalendarOpen(false)}
                              className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-xs font-black text-slate-500 transition hover:bg-slate-50"
                            >
                              Close
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                <p className="text-xs text-gray-400 mt-2">
                  This is required to create a task
                </p>
              </motion.div>

              {/* Priority */}
              <motion.div
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  show: { opacity: 1, y: 0 },
                }}
                className="rounded-[24px] border border-white bg-white/88 p-4 shadow-[0_16px_42px_rgba(15,23,42,0.08)] ring-1 ring-slate-900/5 transition hover:-translate-y-0.5 hover:shadow-[0_22px_55px_rgba(15,23,42,0.1)]"
              >
                <div className="flex items-center justify-between gap-3">
                  <label className="text-sm font-semibold text-slate-700">
                    Priority
                  </label>
                  <span className="rounded-full bg-slate-100 px-3 py-1 text-[11px] font-bold text-slate-500">
                    {priority}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-3 mt-3">
                  {["LOW", "MEDIUM", "HIGH"].map((p) => (
                    <motion.button
                      whileTap={{ scale: 0.9 }}
                      key={p}
                      onClick={() => setPriority(p)}
                      className={`px-4 py-3 rounded-2xl text-sm font-black border transition ${
                        priority === p
                          ? PRIORITY_COLORS[p]
                          : "bg-slate-100/80 text-slate-500 border-slate-200 hover:bg-white hover:text-slate-700 hover:shadow-sm"
                        }`}
                    >
                      {p}
                    </motion.button>
                  ))}
                </div>
              </motion.div>

              {/* Assign */}
              <motion.div
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  show: { opacity: 1, y: 0 },
                }}
                className="rounded-[24px] border border-white bg-white/88 p-4 shadow-[0_16px_42px_rgba(15,23,42,0.08)] ring-1 ring-slate-900/5 transition hover:-translate-y-0.5 hover:shadow-[0_22px_55px_rgba(15,23,42,0.1)]"
              >
                {/* <label className="text-sm font-medium text-gray-700">
                  Assign to
                </label> */}

                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                    <Users size={16} className="text-cyan-600" />
                    Assign to
                    <span className="rounded-full bg-cyan-50 px-2.5 py-1 text-[11px] font-bold text-cyan-700">
                      {assignedTo.length} selected
                    </span>
                  </label>

                  <div className="relative w-full sm:w-48">
                    <Search
                      size={14}
                      className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                    />
                    <input
                      type="text"
                      placeholder="Search member..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="w-full rounded-2xl border border-slate-200 bg-slate-50/90 px-9 py-2.5 text-xs text-slate-800 shadow-inner shadow-slate-200/40 placeholder:text-slate-400 transition focus:bg-white focus:outline-none focus:ring-2 focus:ring-cyan-400"
                    />
                  </div>
                </div>


                <div className="mt-4 max-h-60 space-y-2 overflow-y-auto pr-1">
                  {/* {users.map((u) => ( */}
                  {!assignScopeReady ? (
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-5 text-center text-sm font-semibold text-slate-400">
                      Loading your team...
                    </div>
                  ) : assignableUsers.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-5 text-center">
                      <p className="text-sm font-bold text-slate-600">No assignable members found</p>
                      <p className="mt-1 text-xs text-slate-400">Ask admin to add reporting members first.</p>
                    </div>
                  ) : assignableUsers
                    .filter((u) =>
                      `${u.first_name} ${u.last_name}`
                        .toLowerCase()
                        .includes(search.toLowerCase())
                    )
                    .map((u) => {
                      const selected = assignedTo.includes(u.id);
                      const userImage = getUserImageUrl(u);

                      return (
                        <label
                          key={u.id}
                          className={`group relative flex items-center gap-3 overflow-hidden rounded-[20px] border px-3 py-3 cursor-pointer transition ${
                            selected
                              ? "bg-cyan-50 border-cyan-300 shadow-[0_14px_30px_rgba(6,182,212,0.16)]"
                              : "bg-slate-50/85 hover:bg-white border-slate-200 hover:border-cyan-200 hover:shadow-[0_12px_28px_rgba(15,23,42,0.07)]"
                          }`}
                        >
                          {selected && (
                            <div className="absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-cyan-400 to-blue-500" />
                          )}
                          <input
                            type="checkbox"
                            className="sr-only"
                            checked={selected}
                            onChange={() =>
                              setAssignedTo((prev) =>
                                selected
                                  ? prev.filter((x) => x !== u.id)
                                  : [...prev, u.id]
                              )
                            }
                          />
                          <div className="relative h-11 w-11 shrink-0 overflow-hidden rounded-2xl border border-white shadow-sm">
                            {userImage ? (
                              <img
                                src={userImage}
                                alt={`${u.first_name || ""} ${u.last_name || ""}`.trim()}
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <div className="grid h-full w-full place-items-center bg-[#120d28] text-sm font-black text-cyan-100">
                                {`${u.first_name?.[0] || ""}${u.last_name?.[0] || ""}`}
                              </div>
                            )}
                            {selected && (
                              <div className="absolute inset-0 grid place-items-center bg-cyan-500/80 text-white backdrop-blur-[1px]">
                                <Check size={18} strokeWidth={3} />
                              </div>
                            )}
                          </div>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-black text-slate-800">
                              {u.first_name} {u.last_name}
                            </span>
                            <span className="mt-0.5 flex items-center gap-1 text-[11px] font-semibold text-slate-400">
                              <UserPlus size={12} />
                              {u.isSelfOption ? "Assign yourself" : u.job_title || u.department_name || "Teammate"}
                            </span>
                          </span>
                          <span
                            className={`grid h-7 w-7 shrink-0 place-items-center rounded-full border text-[10px] font-black transition ${
                              selected
                                ? "border-cyan-300 bg-cyan-500 text-white"
                                : "border-slate-200 bg-white text-slate-300 group-hover:text-cyan-500"
                            }`}
                          >
                            {selected ? <Check size={14} strokeWidth={3} /> : "+"}
                          </span>
                        </label>
                      );
                    })}
                </div>

                <div className="text-xs font-medium text-slate-500 mt-3">
                  Select at least one team member to create the task.
                </div>
              </motion.div>

              {error && (
                <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 shadow-sm">
                  {error}
                </div>
              )}
            </motion.div>

            {/* Footer */}
            <div className="px-7 py-4 bg-white/92 backdrop-blur-xl border-t border-slate-200 flex items-center justify-between gap-3 shadow-[0_-14px_35px_rgba(15,23,42,0.08)]">
              <div className="hidden sm:block text-xs font-semibold text-slate-400">
                {assignedTo.length} member{assignedTo.length === 1 ? "" : "s"} selected
              </div>
              <div className="flex justify-end gap-3">
              <button
                onClick={onClose}
                className="px-5 py-2.5 rounded-2xl border border-slate-300 bg-white text-slate-700 font-bold shadow-sm transition hover:bg-slate-50 hover:-translate-y-0.5"
              >
                Cancel
              </button>

              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={handleSubmit}
                disabled={loading}
                className="px-6 py-2.5 rounded-2xl bg-gradient-to-r from-cyan-500 via-blue-500 to-violet-600 text-white font-black shadow-[0_16px_34px_rgba(37,99,235,0.32)] transition hover:-translate-y-0.5 hover:shadow-[0_20px_44px_rgba(37,99,235,0.4)] disabled:opacity-60"
              >
                {loading ? "Creating..." : "Create Task"}
              </motion.button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
}
